import { Injectable, inject } from '@angular/core';
import { SettingsService } from '../settings/settings.service';

@Injectable({
  providedIn: 'root',
})
export class SoundService {
  private settingsService = inject(SettingsService);

  private getSfxVolumeFactor(): number {
    const master = this.settingsService.masterVolume() / 100;
    const sfx = this.settingsService.sfxVolume() / 100;
    return master * sfx;
  }

  public playChime(): void {
    try {
      const ctx = new AudioContext();
      [660, 880].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        const start = ctx.currentTime;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.2 * this.getSfxVolumeFactor(), start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.45); // makes it like fade out
        osc.start(start);
        osc.stop(start + 0.45);
      });
      setTimeout(() => ctx.close(), 1200);
    } catch { /* audio unavailable */ }
  }

  public extraSoundForNow(): void {
    try {
      const ctx = new AudioContext();
      const freqs = [440, 660]; // lower, friendly "enter" tone
      freqs.forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        const start = ctx.currentTime;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.15 * this.getSfxVolumeFactor(), start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.35);
        osc.start(start);
        osc.stop(start + 0.35);
      });
      setTimeout(() => ctx.close(), 800);
    } catch { }
  }

  public playExitRoom(): void {
    try {
      const ctx = new AudioContext();
      const freqs = [660, 440]; // slightly higher "exit" tone
      freqs.forEach((freq) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'triangle'; // change waveform for distinction
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        const start = ctx.currentTime;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.1 * this.getSfxVolumeFactor(), start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.5);
        osc.start(start);
        osc.stop(start + 0.5);
      });
      setTimeout(() => ctx.close(), 1000);
    } catch { }
  }

  public playDirectMessage(): void {
    try {
      const ctx = new AudioContext();
      // Quick, modern double notification beep
      const freqs = [523.25, 659.25]; // C5 to E5
      freqs.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = 'sine';
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        const start = ctx.currentTime + index * 0.1;
        gain.gain.setValueAtTime(0, start);
        gain.gain.linearRampToValueAtTime(0.15 * this.getSfxVolumeFactor(), start + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, start + 0.25);
        osc.start(start);
        osc.stop(start + 0.25);
      });
      setTimeout(() => ctx.close(), 600);
    } catch { }
  }

  public play(fileName: string): void {
    const baseUrl = 'https://static.h5arena.com/sounds/';
    const wavUrl = `${baseUrl}${fileName}.wav`;
    const mp3Url = `${baseUrl}${fileName}.mp3`;

    const audio = new Audio(wavUrl);
    audio.volume = this.getSfxVolumeFactor();
    let triedFallback = false;

    audio.addEventListener('error', () => {
      if (!triedFallback) {
        triedFallback = true;
        const fallbackAudio = new Audio(mp3Url);
        fallbackAudio.volume = this.getSfxVolumeFactor();
        fallbackAudio.play().catch(err => {
          console.warn(`Failed to play fallback audio (${mp3Url}):`, err);
        });
      }
    });

    audio.play().catch(err => {
      if (err && err.name === 'NotAllowedError') {
        console.warn(`Autoplay blocked playing sound: ${fileName}`);
        return;
      }
      if (!triedFallback) {
        triedFallback = true;
        const fallbackAudio = new Audio(mp3Url);
        fallbackAudio.volume = this.getSfxVolumeFactor();
        fallbackAudio.play().catch(fallbackErr => {
          console.warn(`Failed to play fallback audio (${mp3Url}):`, fallbackErr);
        });
      }
    });
  }

  public playChestBuildup(durationSeconds: number): void {
    try {
      const ctx = new AudioContext();
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gainNode = ctx.createGain();

      osc1.type = 'sine';
      osc2.type = 'triangle';

      osc1.connect(gainNode);
      osc2.connect(gainNode);
      gainNode.connect(ctx.destination);

      const start = ctx.currentTime;
      const end = start + durationSeconds;
      const vol = this.getSfxVolumeFactor();

      osc1.frequency.setValueAtTime(110, start);
      osc2.frequency.setValueAtTime(112, start);

      osc1.frequency.exponentialRampToValueAtTime(740, end);
      osc2.frequency.exponentialRampToValueAtTime(745, end);

      gainNode.gain.setValueAtTime(0, start);
      gainNode.gain.linearRampToValueAtTime(0.2 * vol, start + durationSeconds * 0.85);
      gainNode.gain.exponentialRampToValueAtTime(0.001, end);

      osc1.start(start);
      osc1.stop(end);
      osc2.start(start);
      osc2.stop(end);

      setTimeout(() => ctx.close(), (durationSeconds + 0.5) * 1000);
    } catch { }
  }

  public playChestReveal(): void {
    try {
      const ctx = new AudioContext();
      const freqs = [523.25, 659.25, 783.99, 987.77, 1318.51];
      const vol = this.getSfxVolumeFactor();

      freqs.forEach((freq, index) => {
        const osc = ctx.createOscillator();
        const gainNode = ctx.createGain();

        osc.type = index % 2 === 0 ? 'sine' : 'triangle';
        osc.frequency.value = freq;

        osc.connect(gainNode);
        gainNode.connect(ctx.destination);

        const start = ctx.currentTime + (index * 0.06);
        const duration = 1.8;

        gainNode.gain.setValueAtTime(0, start);
        gainNode.gain.linearRampToValueAtTime(0.12 * vol, start + 0.05);
        gainNode.gain.exponentialRampToValueAtTime(0.001, start + duration);

        osc.start(start);
        osc.stop(start + duration);
      });

      setTimeout(() => ctx.close(), 2500);
    } catch { }
  }
}
